import React, { useEffect, useState } from "react";
import {
  SACard,
  SAButton,
  SAIcon,
  SAIcons,
  SAIconSize,
  SAModal,
  SAText,
  SAUXTheme
} from '@saux/design-system-react';
import styled from 'styled-components';
import { decrypt } from '../../../utils/crypto';

const mobileWidth = 600;

type AddContactTypeAlertsProps = {
  isAdmin?: boolean;
  onClickClose: Function;
};

type GeneralDetails = {
  email_address: string,
  first_name: string,
  last_name: string,
  primary_agency_code: string,
  primary_phone: string
}

type StyleProps = {
  error?: boolean;
  theme?: SAUXTheme;
};

const Modal = styled(SAModal)`
  display: flex;
  top: 50%;
  left: 50%;
  justify-content: center;
  align-items: center;
  overflow: auto;
`;

const AdContactTypeAlerts = styled(SACard)`
  ${({ theme }: StyleProps) => {
    return `
      display: flex;
      flex-direction: column;
      width: 650px;
      overflow: auto;
      margin: auto;

      section {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
      }

      @media only screen and (max-width: ${mobileWidth}px) {
        flex-grow: 1;
        width: auto;
        width: 100%;
        height: 100%;
        border-radius: 0;

        section {
          padding: ${theme?.size.spacer.large} ${theme?.size.spacer.medium};
        }
      }
    `;
  }};
`;

const ModalHeader = styled.div`
  padding-bottom: 45px;
`;

const ModalTitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: 30px;

  button:last-child {
    display: none;
    &:focus {
      outline: auto;
    } 
  }

  @media only screen and (max-width: ${mobileWidth}px) {
    button:last-child {
      display: flex;
    }
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalFooter: any = styled.div`
  ${({ theme }: StyleProps) => {
    return `
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      align-items: flex-end;
      padding-top: 60px;

      button {
        font-weight: ${theme?.font.primary.weight.normal};
        margin-left: ${theme?.size.spacer.medium};

        &:first-child {
          margin-left: 0;
        }

        &:last-child {
          font-weight: ${theme?.font.primary.weight.bold};
        }
      }

      @media only screen and (max-width: ${mobileWidth}px) {
        justify-content: center;
        height: 100%;
      }
    `;
  }};
`;

const Text = styled(SAText)`
  font-size: 20px;
  line-height: 26px;
  :first-child {
    padding-bottom: 10px;
  }
`;

export default function ModalPendingEdits({ isAdmin, onClickClose }: AddContactTypeAlertsProps) {
  const generalDetails = JSON.parse(decrypt(localStorage.getItem('general_details')));
  const loggedInPersonId = decrypt(localStorage.getItem('personId'));
  const selectedPersonId = decrypt(localStorage.getItem('selectedPersonId'));
  let personName: string = "";

  if (generalDetails) {
    personName = loggedInPersonId === selectedPersonId ? 'your' : generalDetails.first_name + ' ' + generalDetails.last_name + "'s";
  }

  useEffect(() => {
    function focusDefault() {
      const modal = document.querySelector('#modalpendinedits') as HTMLElement;
      if (modal) {
        const element = modal.querySelector('button.sa-button') as HTMLElement;
        if (element) {
          element.focus();
        }
      } 
    }

    function focusModalPendingEdits(e: any) {
      e.stopPropagation();
      const modal = document.querySelector('#modalpendinedits') as HTMLElement;
      const root = document.querySelector('#root') as HTMLElement;
      if (modal && root) {
        if ((e.type === 'focusout' && root.contains(e.relatedTarget)) || (e.type === 'focusin' && root.contains(e.target))) {
          let element = null;
          if (document.documentElement.clientWidth <= mobileWidth) {
            element = modal.querySelector('button') as HTMLElement;
          } else {
            element = modal.querySelector('a') as HTMLElement;
          }
          if (element) {  
            element.focus();
          }
        }
      }
    }

    focusDefault();
    document.addEventListener('focusin', focusModalPendingEdits);
    document.addEventListener('focusout', focusModalPendingEdits);

    return () => {
      document.removeEventListener('focusin', focusModalPendingEdits);
      document.removeEventListener('focusout', focusModalPendingEdits);
    }
  }, []);

  const modalOnClickHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
  }

  function handleClickPendingEditstModal(event: React.MouseEvent) {
    event.preventDefault();
    // get the card element inside the modal
    // const cardElement = document.querySelector('#modal > div > article > section');

    // close modal when clicking outside of the card element
    // if (!(cardElement instanceof Element && cardElement.contains(event.target as Node))) {
      onClickClose(event);
    // }
  }

  function handleClickClose(event: React.MouseEvent) {
    event.stopPropagation();
    onClickClose(event);
  }

  return (
    <Modal id="modalpendinedits" onClickHandler={(event: React.MouseEvent) => modalOnClickHandler(event)}>
      <AdContactTypeAlerts variant="minimal">
        <ModalHeader>
          <ModalTitle>
            <SAText type="heading-2" text="Need a minute..." />
            <SAIcon className="closemodal" icon={SAIcons.x} size={SAIconSize.medium} colorVariant="dark" onClick={handleClickClose} />
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {!isAdmin ? (
            <Text type="standard">
              While your record is being updated with recent changes, the record is temporarily locked. Please try again later to make your changes. Contact your agency's admin for assistance.
            </Text>
          ) : (
            <Text type="standard">
              While {personName}'s record is being updated with recent changes, the record is temporarily locked. Please try again later to make your changes. Contact <a href="mailto:SalesOperations@stateauto.com">SalesOperations@stateauto.com</a> for assistance.
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          {!isAdmin ? (
            <SAButton label="Return to SAConnect" onClick={handleClickClose} variant="large" color="secondary" textTransform="uppercase" />
          ) : (
            <SAButton label="Return to dashboard" onClick={handleClickClose} variant="large" color="secondary" textTransform="uppercase" />
          )}
        </ModalFooter>
      </AdContactTypeAlerts>
    </Modal>
  );
}
